import { getFinancialYearLabels } from "../../brsrFinancialYearLabels.js";
import {
  BODY_FONT,
  HEADER_FILL,
  hideColumnsAfter,
  hideRowsThrough,
  LINK_FONT,
  NA_FILL,
  THIN_BLACK,
  VALUE_FILL,
  TITLE_FONT,
} from "./detailSheetCommon.js";

const LIFE_CYCLE_ASSESSMENT_SHEET = "Life cycle assessment";
const ACTION_TAKEN_SHEET = "Action taken";
const RECYCLED_OR_REUSED_INPUT_SHEET = "Recycled or reused input";
const RECLAIMED_SHEET = "Reclaimed";
const DETAILS_OF_OTHER_WASTE_SHEET = "Details of other waste";

const PRINCIPLE_TITLE =
  "PRINCIPLE 2 Businesses should provide goods and services in a manner that is sustainable and safe.";

const COL_D = 4;
const COL_E = 5;
const COL_F = 6;
const COL_G = 7;
const COL_H = 8;
const COL_I = 9;
const COL_J = 10;
const COL_END = COL_J;

const FIRST_ROW = 8;
const MIN_ROW_HEIGHT = 15;
const LINE_HEIGHT = 15;

const THIN = { style: "thin", color: { argb: "FF000000" } };
const MEDIUM = { style: "medium", color: { argb: "FF000000" } };

const REF_ROW_HEIGHTS = {
  8: 30,
  9: 30,
  10: 29.25,
  11: 29,
  12: 145,
  13: 72.5,
  14: 15,
  15: 15.75,
  16: 15,
  17: 30,
  18: 30,
  19: 30,
  20: 30,
  21: 20.15,
  22: 30,
  23: 30,
  24: 30,
  25: 34.5,
  26: 20.15,
  27: 48,
  28: 32.25,
  29: 30,
  36: 20.15,
  37: 31.5,
};

const BORDER_PATTERNS = {
  principleTitle: ["mmt-", "m-t-", "m-t-", "m-t-", "m-t-", "m-t-", "m-tm"],
  sectionBand: ["tmt-", "t-t-", "t-t-", "t-t-", "t-t-", "t-t-", "t-tm"],
  questionFull: ["tmt-", "t-t-", "t-t-", "t-t-", "t---", "t---", "t--m"],
  rdHeader: ["-mtt", "-ttt", "-ttt", "-tt-", "tt--", "t---", "t--m"],
  rdData: ["tmtt", "tttt", "tttt", "tttt", "-t--", "----", "---m"],
  rdDataEnd: ["tmtt", "tttt", "tttt", "tt-t", "-t--", "----", "---m"],
  labelValue: ["tmt-", "--t-", "--t-", "tttt", "-t--", "----", "---m"],
  labelValuePercent: ["tmt-", "t-t-", "t-t-", "tttt", "-t--", "----", "---m"],
  labelValueQ4: ["tmt-", "t-t-", "t-tt", "tttt", "-t--", "----", "---m"],
  q3Intro: ["tmt-", "t-t-", "t-t-", "t-tt", "-t--", "----", "---m"],
  reclaimRow: ["tmt-", "t-t-", "t-tt", "tt-t", "-t--", "----", "---m"],
  reclaimLast: ["tmt-", "t-t-", "t-tt", "tttt", "-t--", "----", "---m"],
  eprNoSteps: ["tmt-", "t-t-", "t-tt", "-ttt", "-t--", "--t-", "--tm"],
  leadershipQ: ["tmtt", "tttt", "tttt", "tttt", "tt--", "tttt", "tt-m"],
  leadershipLink: ["tmt-", "t-t-", "t-t-", "t-tt", "tt-t", "tt--", "---m"],
  leadershipLinkWide: ["tmtt", "tttt", "tttt", "tttt", "tt-t", "-t--", "---m"],
  leadershipLinkWide2: ["tmtt", "tttt", "tttt", "tttt", "tttt", "-t--", "---m"],
  packagingIntro: ["tmt-", "t-t-", "t-t-", "t-t-", "t-tt", "-tt-", "--tm"],
  packagingFy: ["tm-t", "tttt", "tttt", "tttt", "tttt", "-ttt", "-ttm"],
  packagingSub: ["-mtt", "tt-t", "tt-t", "tt-t", "tt-t", "tt-t", "tt-m"],
  packagingData: ["tmt-", "tttt", "tttt", "tttt", "tttt", "tttt", "tttm"],
  packagingOther: ["tmtt", "-tt-", "-tt-", "--t-", "--t-", "--t-", "--tm"],
  reclaimedLink: ["tmtt", "tttt", "tttt", "ttt-", "tttt", "--t-", "--tm"],
  notesRow: ["-mm-", "ttmt", "ttm-", "t-m-", "t-m-", "t-m-", "t-mm"],
};

const LABELS = {
  essential: "Essential Indicators",
  q1:
    "1. Percentage of R&D and capital expenditure (capex) investments in specific technologies to improve the environmental and social impacts of product and processes to total R&D and capex investments made by the entity, respectively.",
  detailsHeader: "Details of improvements in environmental and social impacts",
  q2a: "2.  a. Does the entity have procedures in place for sustainable sourcing? (Yes/No)",
  q2b: "b. If yes, what percentage of inputs were sourced sustainably?",
  q3:
    "3. Describe the processes in place to safely reclaim your products for reusing, recycling and disposing at the end of life, for",
  q4: "4. Whether Extended Producer Responsibility (EPR) is applicable to the entity's activities (Yes / No).",
  q4Yes:
    "If yes, whether the waste collection plan is in line with the Extended Producer Responsibility (EPR) plan submitted to Pollution Control Boards? (Yes/No)",
  q4No: "If not, provide steps taken to address the same.",
  leadership: "Leadership Indicators",
  lq1:
    "1. Has the entity conducted Life Cycle Perspective / Assessments (LCA) for any of its products (for manufacturing industry) or for its services (for service industry)?",
  lq1Details: "If yes, provide details",
  lq2:
    "2. If there are any significant social or environmental concerns and/or risks arising from production or disposal of your products / services, as identified in the Life Cycle Perspective / Assessments (LCA) or through any other means, briefly describe the same along-with action taken to mitigate the same.",
  lq3:
    "3. Percentage of recycled or reused input material to total material (by value) used in production (for manufacturing industry) or providing services (for service industry).",
  lq4:
    "4. Of the products and packaging reclaimed at end of life of products, amount (in metric tonnes) reused, recycled, and safely disposed:",
  lq5:
    "5. Reclaimed products and their packaging materials (as percentage of products sold) for each product category",
  notes: "Notes",
};

function parseBorderSpec(spec) {
  const map = { t: THIN, m: MEDIUM };
  return {
    top: spec[0] !== "-" ? map[spec[0]] : undefined,
    left: spec[1] !== "-" ? map[spec[1]] : undefined,
    bottom: spec[2] !== "-" ? map[spec[2]] : undefined,
    right: spec[3] !== "-" ? map[spec[3]] : undefined,
  };
}

function borderFromPattern(patternKey, col) {
  const pattern = BORDER_PATTERNS[patternKey];
  const spec = pattern?.[col - COL_D];
  return spec ? parseBorderSpec(spec) : THIN_BLACK;
}

function applyRowBorders(ws, row, patternKey) {
  const pattern = BORDER_PATTERNS[patternKey];
  if (!pattern) return;
  pattern.forEach((spec, index) => {
    const cell = ws.getCell(row, COL_D + index);
    cell.border = parseBorderSpec(spec);
    if (!cell.font) cell.font = BODY_FONT;
  });
}

function finishRow(ws, row, patternKey, height) {
  applyRowBorders(ws, row, patternKey);
  setRowHeight(ws, row, height);
}

function applyOuterMergeBorder(ws, row, startCol, endCol, patternKey) {
  const pattern = BORDER_PATTERNS[patternKey];
  if (!pattern) return;
  if (pattern.length === 1) {
    ws.getCell(row, startCol).border = parseBorderSpec(pattern[0]);
    return;
  }
  const left = parseBorderSpec(pattern[startCol - COL_D]);
  const right = parseBorderSpec(pattern[endCol - COL_D]);
  ws.getCell(row, startCol).border = {
    top: left.top,
    left: left.left,
    bottom: right.bottom,
    right: right.right,
  };
}

function setRowHeight(ws, row, height) {
  if (height != null) ws.getRow(row).height = height;
  else if (REF_ROW_HEIGHTS[row] != null) ws.getRow(row).height = REF_ROW_HEIGHTS[row];
}

function mergeRow(ws, row, startCol, endCol, value, options = {}) {
  if (startCol < endCol) ws.mergeCells(row, startCol, row, endCol);
  setCell(ws, row, startCol, value, { ...options, bordered: false });
}

function mergeRowAfterBorders(ws, row, startCol, endCol, value, options = {}) {
  setCell(ws, row, startCol, value, { ...options, bordered: false });
  if (startCol < endCol) ws.mergeCells(row, startCol, row, endCol);
}

function padMergeRow(ws, row, startCol, endCol, patternKey) {
  for (let col = startCol + 1; col <= endCol; col += 1) {
    const cell = ws.getCell(row, col);
    if (cell.value === null || cell.value === undefined) cell.value = "";
  }
  if (startCol < endCol) {
    ws.mergeCells(row, startCol, row, endCol);
    if (patternKey) {
      const master = ws.getCell(row, startCol);
      const rightBorder = borderFromPattern(patternKey, endCol);
      master.border = { ...master.border, right: rightBorder.right };
    }
  }
}

function setEmptyCell(ws, row, col, patternKey) {
  setCell(ws, row, col, "", {
    bordered: false,
    border: borderFromPattern(patternKey, col),
  });
}

function setGreyValueCell(ws, row, col, value, patternKey, options = {}) {
  setCell(ws, row, col, safeText(value, ""), {
    align: "center",
    bordered: false,
    border: borderFromPattern(patternKey, col),
    ...options,
    fill: NA_FILL,
  });
}

function setWhiteValueCell(ws, row, col, value, patternKey, align = "center") {
  setCell(ws, row, col, safeText(value, ""), {
    align,
    fill: VALUE_FILL,
    bordered: false,
    border: patternKey ? borderFromPattern(patternKey, col) : THIN_BLACK,
  });
}

function setNaCell(ws, row, col, patternKey) {
  setCell(ws, row, col, "", {
    fill: NA_FILL,
    bordered: false,
    border: borderFromPattern(patternKey, col),
  });
}

function applyGreyBandFill(ws, row, startCol = COL_D, endCol = COL_END) {
  for (let col = startCol; col <= endCol; col += 1) {
    ws.getCell(row, col).fill = NA_FILL;
  }
}

function applyTrailingEmptyCells(ws, row, patternKey) {
  for (let col = COL_H; col <= COL_J; col += 1) {
    setNaCell(ws, row, col, patternKey);
  }
}

function setDetailValueCell(ws, row, col, value, patternKey, options = {}) {
  setCell(ws, row, col, safeText(value, ""), {
    align: "center",
    bordered: false,
    border: borderFromPattern(patternKey, col),
    ...options,
  });
}

function applyDetailBlock(ws, row, value, patternKey, options = {}) {
  setDetailValueCell(ws, row, COL_G, value, patternKey, options);
  applyTrailingEmptyCells(ws, row, patternKey);
}

function estimateRowHeight(text, colWidth, minHeight = MIN_ROW_HEIGHT) {
  const str = String(text ?? "");
  if (!str.trim()) return minHeight;
  const charsPerLine = Math.max(8, Math.floor(colWidth * 0.85));
  const lineCount = str.split(/\r?\n/).reduce(
    (total, part) => total + Math.max(1, Math.ceil(part.length / charsPerLine)),
    0
  );
  return Math.max(minHeight, lineCount * LINE_HEIGHT + 8);
}

function setAutoRowHeight(ws, row, parts) {
  ws.getRow(row).height = parts.reduce(
    (max, { text, width }) => Math.max(max, estimateRowHeight(text, width)),
    MIN_ROW_HEIGHT
  );
  if (REF_ROW_HEIGHTS[row] != null) ws.getRow(row).height = REF_ROW_HEIGHTS[row];
}

function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.formula) {
    cell.value = { formula: options.formula };
  } else if (options.link) {
    cell.value = { text: value || "Add Details", hyperlink: options.link };
    cell.font = LINK_FONT;
  } else {
    cell.value = value;
  }

  if (!options.link) {
    if (options.font) {
      cell.font =
        options.bold !== undefined ? { ...options.font, bold: options.bold } : options.font;
    } else {
      cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
    }
  }

  if (options.border) {
    cell.border = options.border;
  } else if (options.bordered !== false) {
    cell.border = THIN_BLACK;
  }
  cell.alignment = {
    horizontal: options.align ?? (options.link ? "center" : "left"),
    vertical: options.vertical || "middle",
    wrapText: options.wrapText !== false,
  };
  if (options.fill) {
    cell.fill = options.fill;
  }
}

function applyPartialRowBorders(ws, row, patternKey, colStart, colEnd) {
  const pattern = BORDER_PATTERNS[patternKey];
  if (!pattern) return;
  for (let col = colStart; col <= colEnd; col += 1) {
    const spec = pattern[col - COL_D];
    const cell = ws.getCell(row, col);
    if (spec) cell.border = parseBorderSpec(spec);
    if (!cell.font) cell.font = BODY_FONT;
  }
}

function styleUnmergedRow(ws, row, patternKey, height) {
  finishRow(ws, row, patternKey, height);
}

function addDetailLink(ws, row, col, sheetName, targetCell = "D5", { fill = VALUE_FILL, patternKey } = {}) {
  const options = {
    align: "center",
    link: `#'${sheetName}'!${targetCell}`,
    bordered: false,
    border: patternKey ? borderFromPattern(patternKey, col) : THIN_BLACK,
  };
  if (fill) options.fill = fill;
  setCell(ws, row, col, "Add Details", options);
}

function safeText(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;
  if (Array.isArray(value)) return value.length ? value.join(", ") : fallback;
  if (typeof value === "object") return fallback;
  return String(value);
}

function toYesNo(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  return "";
}

function formatRatio(value) {
  if (value === null || value === undefined || value === "") return "";
  const str = String(value).replace("%", "").trim();
  const num = Number(str);
  if (Number.isNaN(num)) return str;
  const ratio = num > 1 ? num / 100 : num;
  const fixed = ratio.toFixed(4);
  return fixed.replace(/\.?0+$/, "") || "0";
}

function formatAmount(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  if (!Number.isNaN(num)) {
    return Number.isInteger(num) ? String(num) : String(num);
  }
  return String(value);
}

function setDataCell(ws, row, col, value, align = "center", patternKey) {
  setWhiteValueCell(ws, row, col, value, patternKey, align);
}

function hideGridLines(workbook, ws) {
  ws.views = [
    {
      showGridLines: false,
      state: "frozen",
      xSplit: 3,
      ySplit: 8,
      topLeftCell: "D29",
      zoomScale: 80,
      zoomScaleNormal: 80,
    },
  ];
  if (!workbook.views?.length) {
    workbook.views = [{ showGridLines: false }];
  } else {
    workbook.views[0].showGridLines = false;
  }
}

function setupPrinciple2Sheet(workbook, ws) {
  const widths = {
    A: 9.1796875,
    B: 9.1796875,
    C: 9.453125,
    D: 29.7265625,
    E: 30.7265625,
    F: 37,
    G: 30.7265625,
    H: 30.7265625,
    I: 30.7265625,
    J: 30.7265625,
    K: 9.1796875,
    L: 9.1796875,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  ws.getColumn("A").hidden = true;
  ws.getColumn("B").hidden = true;
  // Visible through L; hide M → XFD.
  hideColumnsAfter(ws, 12);
  hideRowsThrough(ws, 5);
  hideGridLines(workbook, ws);
}

function addPrincipleTitle(ws, row) {
  for (let col = COL_D; col <= COL_J; col += 1) {
    setCell(ws, row, col, col === COL_D ? PRINCIPLE_TITLE : "", {
      font: TITLE_FONT,
      align: "center",
      fill: HEADER_FILL,
      bordered: false,
    });
  }
  finishRow(ws, row, "principleTitle", 30);
  padMergeRow(ws, row, COL_D, COL_END, "principleTitle");
  return row + 1;
}

function addSectionBand(ws, row, title) {
  for (let col = COL_D; col <= COL_J; col += 1) {
    setCell(ws, row, col, col === COL_D ? title : "", {
      font: TITLE_FONT,
      align: "center",
      fill: NA_FILL,
      bordered: false,
    });
  }
  finishRow(ws, row, "sectionBand");
  padMergeRow(ws, row, COL_D, COL_END, "sectionBand");
  return row + 1;
}

function addRdCapexTable(ws, row, essential, fyCurrent, fyPrevious) {
  for (let col = COL_D; col <= COL_J; col += 1) {
    setCell(ws, row, col, col === COL_D ? LABELS.q1 : "", {
      align: "left",
      vertical: "top",
      bordered: false,
    });
  }
  finishRow(ws, row, "questionFull");
  padMergeRow(ws, row, COL_D, COL_END, "questionFull");
  row += 1;

  setEmptyCell(ws, row, COL_D, "rdHeader");
  setCell(ws, row, COL_E, fyCurrent, {
    bold: true,
    bordered: false,
    align: "center",
    border: borderFromPattern("rdHeader", COL_E),
  });
  setCell(ws, row, COL_F, fyPrevious, {
    bold: true,
    bordered: false,
    align: "center",
    border: borderFromPattern("rdHeader", COL_F),
  });
  setCell(ws, row, COL_G, LABELS.detailsHeader, {
    align: "center",
    bordered: false,
    border: borderFromPattern("rdHeader", COL_G),
  });
  applyTrailingEmptyCells(ws, row, "rdHeader");
  finishRow(ws, row, "rdHeader");
  row += 1;

  const categories = [
    ["R&D", essential?.rAndD],
    ["Capex", essential?.CapexInvestments],
  ];

  categories.forEach(([label, data], idx) => {
    const pattern = idx === categories.length - 1 ? "rdDataEnd" : "rdData";
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: borderFromPattern(pattern, COL_D),
    });
    setWhiteValueCell(ws, row, COL_E, formatRatio(data?.currentFinancialYear), pattern, "center");
    setWhiteValueCell(ws, row, COL_F, formatRatio(data?.previousFinancialYear), pattern, "center");
    applyDetailBlock(ws, row, data?.detailsOfImprovements, pattern, {
      align: "left",
      vertical: "top",
    });
    finishRow(ws, row, pattern);
    if (idx === 0) {
      setAutoRowHeight(ws, row, [{ text: data?.detailsOfImprovements, width: 30 }]);
    }
    row += 1;
  });

  return row;
}

function addLabelValueRow(ws, row, label, value, patternKey = "labelValue") {
  setCell(ws, row, COL_D, label, { align: "left", vertical: "top", bordered: false });
  setCell(ws, row, COL_E, "", { bordered: false });
  setCell(ws, row, COL_F, "", { bordered: false });
  applyDetailBlock(ws, row, value, patternKey);
  finishRow(ws, row, patternKey);
  padMergeRow(ws, row, COL_D, COL_F);
  return row + 1;
}

function addReclaimSection(ws, row, essential) {
  for (let col = COL_D; col <= COL_J; col += 1) {
    setCell(ws, row, col, col === COL_D ? LABELS.q3 : "", {
      align: "left",
      vertical: "top",
      bordered: false,
    });
  }
  applyTrailingEmptyCells(ws, row, "q3Intro");
  finishRow(ws, row, "q3Intro");
  padMergeRow(ws, row, COL_D, COL_G);
  row += 1;

  const items = [
    ["(a) Plastics (including packaging)", essential?.productReclaimProcesses?.plastics],
    ["(b) E-waste", essential?.productReclaimProcesses?.eWaste],
    ["(c) Hazardous waste", essential?.productReclaimProcesses?.hazardousWaste],
    ["(d) other waste", essential?.productReclaimProcesses?.otherWaste],
  ];

  items.forEach(([label, text], idx) => {
    const pattern = idx === items.length - 1 ? "reclaimLast" : "reclaimRow";
    setCell(ws, row, COL_D, label, { align: "left", bordered: false });
    setCell(ws, row, COL_E, "", { bordered: false });
    setCell(ws, row, COL_F, "", { bordered: false });
    applyDetailBlock(ws, row, text, pattern);
    finishRow(ws, row, pattern);
    padMergeRow(ws, row, COL_D, COL_F);
    row += 1;
  });

  return row;
}

function addEprSection(ws, row, essential) {
  row = addLabelValueRow(ws, row, LABELS.q4, toYesNo(essential?.eprApplicable), "labelValueQ4");

  row = addLabelValueRow(
    ws,
    row,
    LABELS.q4Yes,
    toYesNo(essential?.eprWasteCollectionPlan?.status),
    "labelValueQ4"
  );

  setCell(ws, row, COL_D, LABELS.q4No, { align: "left", vertical: "top", bordered: false });
  setCell(ws, row, COL_E, "", { bordered: false });
  setCell(ws, row, COL_F, "", { bordered: false });
  const steps =
    essential?.eprApplicable && essential?.eprWasteCollectionPlan?.status === false
      ? safeText(essential?.eprWasteCollectionPlan?.remark, "")
      : "";
  const stepsPattern = steps ? "labelValueQ4" : "eprNoSteps";
  applyDetailBlock(ws, row, steps, stepsPattern, {
    align: "left",
    vertical: "top",
    fill: steps ? VALUE_FILL : NA_FILL,
  });
  finishRow(ws, row, stepsPattern);
  padMergeRow(ws, row, COL_D, COL_F);
  row += 1;

  return row;
}

function addLeadershipQuestion(ws, row, label, value) {
  for (let col = COL_D; col <= COL_G; col += 1) {
    setCell(ws, row, col, col === COL_D ? label : "", {
      align: "left",
      vertical: "top",
      bordered: false,
    });
  }
  setGreyValueCell(ws, row, COL_H, value, "leadershipQ");
  setNaCell(ws, row, COL_I, "leadershipQ");
  setNaCell(ws, row, COL_J, "leadershipQ");
  finishRow(ws, row, "leadershipQ");
  padMergeRow(ws, row, COL_D, COL_G);
  setAutoRowHeight(ws, row, [{ text: label, width: 120 }]);
  return row + 1;
}

function addLeadershipLinkRow(
  ws,
  row,
  label,
  sheetName,
  {
    patternKey = "leadershipLink",
    targetCell = "D5",
    linkFill = VALUE_FILL,
    labelMergeFill,
    widePatternVariant = 1,
  } = {}
) {
  const widePattern =
    widePatternVariant === 2 ? "leadershipLinkWide2" : "leadershipLinkWide";
  const pattern = patternKey === "leadershipLinkWide" ? widePattern : patternKey;

  if (labelMergeFill) {
    setCell(ws, row, COL_D, label, { align: "left", vertical: "top", bordered: false });
    setCell(ws, row, COL_E, "", { bordered: false });
    setCell(ws, row, COL_F, "", { bordered: false });
    setEmptyCell(ws, row, COL_G, pattern);
  } else {
    for (let col = COL_D; col <= COL_G; col += 1) {
      setCell(ws, row, col, col === COL_D ? label : "", {
        align: "left",
        vertical: "top",
        bordered: false,
      });
    }
  }
  addDetailLink(ws, row, COL_H, sheetName, targetCell, {
    fill: linkFill ?? NA_FILL,
    patternKey: pattern,
  });
  setNaCell(ws, row, COL_I, pattern);
  setNaCell(ws, row, COL_J, pattern);
  finishRow(ws, row, pattern);
  if (labelMergeFill) {
    padMergeRow(ws, row, COL_D, COL_F);
  } else {
    padMergeRow(ws, row, COL_D, COL_G);
  }
  return row + 1;
}

function addPackagingTable(ws, row, leadership, fyCurrent, fyPrevious) {
  for (let col = COL_D; col <= COL_H; col += 1) {
    setCell(ws, row, col, col === COL_D ? LABELS.lq4 : "", {
      align: "left",
      vertical: "top",
      bordered: false,
    });
  }
  setEmptyCell(ws, row, COL_I, "packagingIntro");
  setEmptyCell(ws, row, COL_J, "packagingIntro");
  finishRow(ws, row, "packagingIntro");
  padMergeRow(ws, row, COL_D, COL_H);
  row += 1;

  const fyRow = row;
  const subRow = fyRow + 1;

  setCell(ws, fyRow, COL_D, "", { bordered: false });
  setCell(ws, subRow, COL_D, "", { bordered: false });
  setCell(ws, fyRow, COL_E, fyCurrent, { bold: true, bordered: false, align: "center" });
  setCell(ws, fyRow, COL_F, "", { bordered: false });
  setCell(ws, fyRow, COL_G, "", { bordered: false });
  setCell(ws, fyRow, COL_H, fyPrevious, { bold: true, bordered: false, align: "center" });
  setCell(ws, fyRow, COL_I, "", { bordered: false });
  setCell(ws, fyRow, COL_J, "", { bordered: false });
  finishRow(ws, fyRow, "packagingFy");
  ws.mergeCells(fyRow, COL_D, subRow, COL_D);
  ws.mergeCells(fyRow, COL_E, fyRow, COL_G);
  ws.mergeCells(fyRow, COL_H, fyRow, COL_J);
  applyGreyBandFill(ws, fyRow, COL_D, COL_J);
  ws.getCell(subRow, COL_D).fill = NA_FILL;

  ["Re-Used", "Recycled", "Safely\nDisposed", "Re-Used", "Recycled", "Safely\nDisposed"].forEach(
    (label, idx) => {
      const col = COL_E + idx;
      setCell(ws, subRow, col, label, {
        align: "center",
        fill: NA_FILL,
        bordered: false,
        border: borderFromPattern("packagingSub", col),
      });
    }
  );
  finishRow(ws, subRow, "packagingSub");
  row = subRow + 1;

  const packaging = leadership?.reclaimedProductsAndPackaging || {};
  const categories = [
    ["Plastics (including packaging)", "plastics"],
    ["E waste", "eWaste"],
    ["Hazardous waste", "hazardousWaste"],
  ];

  categories.forEach(([label, key]) => {
    const current = packaging?.currentFinancialYear?.[key] || {};
    const previous = packaging?.previousFinancialYear?.[key] || {};
    setCell(ws, row, COL_D, label, {
      align: "left",
      bordered: false,
      border: borderFromPattern("packagingData", COL_D),
    });
    setDataCell(ws, row, COL_E, formatAmount(current?.reUsed), "center", "packagingData");
    setDataCell(ws, row, COL_F, formatAmount(current?.recycled), "center", "packagingData");
    setDataCell(ws, row, COL_G, formatAmount(current?.safelyDisposed), "center", "packagingData");
    setDataCell(ws, row, COL_H, formatAmount(previous?.reUsed), "center", "packagingData");
    setDataCell(ws, row, COL_I, formatAmount(previous?.recycled), "center", "packagingData");
    setDataCell(ws, row, COL_J, formatAmount(previous?.safelyDisposed), "center", "packagingData");
    finishRow(ws, row, "packagingData");
    row += 1;
  });

  setCell(ws, row, COL_D, "Other waste", {
    align: "left",
    bordered: false,
    border: borderFromPattern("packagingOther", COL_D),
  });
  addDetailLink(ws, row, COL_E, DETAILS_OF_OTHER_WASTE_SHEET, "D6", {
    fill: null,
    patternKey: "packagingOther",
  });
  for (let col = COL_F; col <= COL_J; col += 1) {
    setNaCell(ws, row, col, "packagingOther");
  }
  finishRow(ws, row, "packagingOther");
  row += 1;

  return row;
}

function hasLcaConducted(leadership) {
  const assessments = leadership?.lifeCycleAssessments?.assessments;
  if (Array.isArray(assessments) && assessments.length > 0) return true;
  return !!leadership?.lifeCycleAssessments?.status;
}

export function buildPrinciple2Sheet(workbook, sectionC, reportingYear = "") {
  const ws = workbook.addWorksheet("PRINCIPLE 2", { views: [{ showGridLines: false }] });
  setupPrinciple2Sheet(workbook, ws);

  const principle = sectionC?.principle2 || {};
  const essential = principle?.essentialIndicators || {};
  const leadership = principle?.leadershipIndicator || {};
  const fyLabels = getFinancialYearLabels(reportingYear);
  const fyCurrent = fyLabels.currentYear
    ? `FY (${fyLabels.currentYear})`
    : "FY (Current Financial Year)";
  const fyPrevious = fyLabels.previousYear
    ? `PY (${fyLabels.previousYear})`
    : "PY (Previous Financial Year)";

  let row = FIRST_ROW;
  row = addPrincipleTitle(ws, row);
  row = addSectionBand(ws, row, LABELS.essential);
  row = addRdCapexTable(ws, row, essential, fyCurrent, fyPrevious);

  row = addLabelValueRow(ws, row, LABELS.q2a, toYesNo(essential?.sustainableSourcing?.status));
  row = addLabelValueRow(
    ws,
    row,
    LABELS.q2b,
    formatRatio(essential?.sustainableSourcing?.remark),
    "labelValuePercent"
  );

  row = addReclaimSection(ws, row, essential);
  row = addEprSection(ws, row, essential);

  row = addSectionBand(ws, row, LABELS.leadership);

  row = addLeadershipQuestion(ws, row, LABELS.lq1, toYesNo(hasLcaConducted(leadership)));
  row = addLeadershipLinkRow(ws, row, LABELS.lq1Details, LIFE_CYCLE_ASSESSMENT_SHEET, {
    labelMergeFill: NA_FILL,
  });
  row = addLeadershipLinkRow(ws, row, LABELS.lq2, ACTION_TAKEN_SHEET, {
    patternKey: "leadershipLinkWide",
    widePatternVariant: 1,
  });
  row = addLeadershipLinkRow(ws, row, LABELS.lq3, RECYCLED_OR_REUSED_INPUT_SHEET, {
    patternKey: "leadershipLinkWide",
    targetCell: "D6",
    widePatternVariant: 2,
  });

  row = addPackagingTable(ws, row, leadership, fyCurrent, fyPrevious);

  for (let col = COL_D; col <= COL_G; col += 1) {
    setCell(ws, row, col, col === COL_D ? LABELS.lq5 : "", {
      align: "left",
      vertical: "top",
      bordered: false,
    });
  }
  addDetailLink(ws, row, COL_H, RECLAIMED_SHEET, "D5", {
    fill: null,
    patternKey: "reclaimedLink",
  });
  setNaCell(ws, row, COL_I, "reclaimedLink");
  setNaCell(ws, row, COL_J, "reclaimedLink");
  finishRow(ws, row, "reclaimedLink", 20.15);
  padMergeRow(ws, row, COL_D, COL_G);
  row += 1;

  setCell(ws, row, COL_D, LABELS.notes, {
    align: "left",
    bordered: false,
    border: borderFromPattern("notesRow", COL_D),
  });
  setCell(ws, row, COL_E, safeText(principle?.notes, ""), {
    align: "left",
    vertical: "top",
    bordered: false,
    border: borderFromPattern("notesRow", COL_E),
  });
  for (let col = COL_F; col <= COL_J; col += 1) {
    setNaCell(ws, row, col, "notesRow");
  }
  finishRow(ws, row, "notesRow", 31.5);

  hideRowsThrough(ws, 5);
  return ws;
}
